import { RiseOutlined, TrophyOutlined, UserOutlined } from '@ant-design/icons';
import { Col, Row, Skeleton, Tooltip, Typography } from 'antd';
import React from 'react';
import { glassCardStyle } from '../../Performance/utils';

const { Title, Text } = Typography;

interface DashboardSummaryCardsProps {
  statsData: any;
  user: any;
  loading: boolean;
  navigate?: any;
  leadStatuses?: any[];
}

export const DashboardSummaryCards: React.FC<DashboardSummaryCardsProps> = ({
  statsData,
  user,
  loading,
  navigate,
  leadStatuses,
}) => {
  const isCardLoading = loading || !statsData;
  const isSalesInhouse = user?.role?.name === 'sales_inhouse' && statsData?.target_progress;

  // Calculate layout column widths dynamically
  const colSpan = isSalesInhouse ? { xs: 24, sm: 12, lg: 8 } : { xs: 24, sm: 12, lg: 12 };

  const handleNewLeadsClick = () => {
    if (!navigate) return;
    const newStatus = leadStatuses?.find(
      (s: any) => s.name?.toLowerCase().includes('new') || s.name?.toLowerCase().includes('baru')
    );
    if (newStatus) {
      navigate(`/leads/data?tahapanFU=${newStatus.id}`);
    } else {
      navigate('/leads/data');
    }
  };

  return (
    <Row gutter={[20, 20]} style={{ marginBottom: '24px' }}>
      {/* Target Progress (Only for Sales Inhouse) */}
      {isSalesInhouse && (
        <Col {...colSpan}>
          <Tooltip title="Klik untuk melihat data leads Anda">
            <div
              style={{ ...glassCardStyle, cursor: 'pointer' }}
              className="glass-card-interactive"
              onClick={() => navigate?.('/leads/data')}
            >
              {isCardLoading ? (
                <Skeleton active paragraph={{ rows: 2 }} title={false} style={{ margin: '8px 0' }} />
              ) : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <Text type="secondary" strong style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Target Leads Anda ({statsData.target_progress.year})
                      </Text>
                      <Title level={3} style={{ margin: '8px 0 0 0', color: '#0f172a', fontWeight: 800 }}>
                        {statsData.target_progress.realization}{' '}
                        <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#64748b' }}>
                          dari {statsData.target_progress.target}
                        </span>
                      </Title>
                    </div>
                    <div style={{ padding: '10px', background: 'rgba(250, 173, 20, 0.15)', borderRadius: '10px' }}>
                      <TrophyOutlined style={{ fontSize: '22px', color: '#faad14' }} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Pencapaian target leads pribadi pada tahun berjalan.
                    </Text>
                    <span style={{ fontSize: '11px', color: '#7BAA18', fontWeight: 600 }}>Lihat data →</span>
                  </div>
                </>
              )}
            </div>
          </Tooltip>
        </Col>
      )}

      {/* KPI: Total Leads */}
      <Col {...colSpan}>
        <Tooltip title="Klik untuk membuka semua data leads">
          <div
            style={{ ...glassCardStyle, cursor: 'pointer' }}
            className="glass-card-interactive"
            onClick={() => navigate?.('/leads/data')}
          >
            {isCardLoading ? (
              <Skeleton active paragraph={{ rows: 2 }} title={false} style={{ margin: '8px 0' }} />
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <Text type="secondary" strong style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Total Leads
                    </Text>
                    <Title level={3} style={{ margin: '8px 0 0 0', color: '#0f172a', fontWeight: 800 }}>
                      {new Intl.NumberFormat('id-ID').format(statsData.total_leads)}{' '}
                      <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#64748b' }}>Lead</span>
                    </Title>
                  </div>
                  <div style={{ padding: '10px', background: 'rgba(24, 144, 255, 0.15)', borderRadius: '10px' }}>
                    <UserOutlined style={{ fontSize: '22px', color: '#1890ff' }} />
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Seluruh data prospek yang tersimpan dalam sistem.
                  </Text>
                  <span style={{ fontSize: '11px', color: '#7BAA18', fontWeight: 600 }}>Lihat semua →</span>
                </div>
              </>
            )}
          </div>
        </Tooltip>
      </Col>

      {/* KPI: Leads Baru (Bulan Ini) */}
      <Col {...colSpan}>
        <Tooltip title="Klik untuk menyaring leads baru yang belum di-follow-up">
          <div
            style={{ ...glassCardStyle, cursor: 'pointer' }}
            className="glass-card-interactive"
            onClick={handleNewLeadsClick}
          >
            {isCardLoading ? (
              <Skeleton active paragraph={{ rows: 2 }} title={false} style={{ margin: '8px 0' }} />
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <Text type="secondary" strong style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Leads Baru Belum Terfollow-up (Bulan Ini)
                    </Text>
                    <Title level={3} style={{ margin: '8px 0 0 0', color: '#0f172a', fontWeight: 800 }}>
                      {new Intl.NumberFormat('id-ID').format(statsData.new_leads)}{' '}
                      <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#64748b' }}>Lead</span>
                    </Title>
                  </div>
                  <div style={{ padding: '10px', background: 'rgba(82, 196, 26, 0.15)', borderRadius: '10px' }}>
                    <RiseOutlined style={{ fontSize: '22px', color: '#52c41a' }} />
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Jumlah Lead baru masuk bulan ini dan belum ada follow-up.
                  </Text>
                  <span style={{ fontSize: '11px', color: '#7BAA18', fontWeight: 600 }}>Filter leads baru →</span>
                </div>
              </>
            )}
          </div>
        </Tooltip>
      </Col>
    </Row>
  );
};

export default DashboardSummaryCards;
